/**
 * @ipc/validation · src/schemas/admin-users.ts — zod-схемы (Zod v4) для границ
 * админ-пользователей (`/admin/users`, раздел 09/14). Валидирует тела создания
 * администратора (tgUsername + roleId) и частичного обновления (isActive и/или
 * roleId) — до попадания в сервис. Аутентификация — через Telegram Login Widget,
 * пароля/email нет: админ заводится по Telegram username, telegramId привязывается
 * при первом входе.
 */
import { z } from 'zod/v4';
import { paginationSchema, searchSchema, sortOrderSchema, booleanQuerySchema } from '../common/primitives.ts';

/**
 * Telegram username: 5–32 символа, буквы/цифры/underscore (правило Telegram).
 * Нормализуем к нижнему регистру и снимаем ведущий `@` — по этому значению
 * потом матчится Login Widget. Единый источник для create/форм/привязки.
 */
export const tgUsernameSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/^@/, '').toLowerCase())
  .pipe(z.string().regex(/^[a-z0-9_]{5,32}$/, 'Некорректный Telegram username (5–32 символа: буквы, цифры, _)'));

export const createAdminUserSchema = z.object({
  tgUsername: tgUsernameSchema,
  roleId: z.coerce.number().int().positive(),
});

export const updateAdminUserSchema = z
  .object({
    isActive: z.boolean().optional(),
    roleId: z.coerce.number().int().positive().optional(),
  })
  .refine(
    (v) => v.isActive !== undefined || v.roleId !== undefined,
    'Нужно указать хотя бы одно поле: isActive или roleId',
  );

export const adminUserIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});

export type CreateAdminUserDto = z.infer<typeof createAdminUserSchema>;
export type UpdateAdminUserDto = z.infer<typeof updateAdminUserSchema>;
export type AdminUserIdParamDto = z.infer<typeof adminUserIdParamSchema>;

/**
 * Форма создания администратора в admin-ui: та же граница, но БЕЗ z.coerce на roleId
 * (react-hook-form + zodResolver требуют совпадения input/output типов схемы; roleId
 * форма всегда пишет как number через Select.onChange) и с русскими сообщениями для
 * мгновенного feedback. Тело на wire идентично createAdminUserSchema — backend
 * валидирует своей схемой независимо.
 */
export const createAdminUserFormSchema = z.object({
  tgUsername: z
    .string({ error: 'Укажите Telegram username' })
    .trim()
    .transform((v) => v.replace(/^@/, '').toLowerCase())
    .pipe(
      z
        .string()
        .regex(/^[a-z0-9_]{5,32}$/, 'Некорректный Telegram username (5–32 символа: буквы, цифры, _)'),
    ),
  roleId: z.number({ error: 'Выберите роль администратора' }).int().positive('Выберите роль администратора'),
});
export type CreateAdminUserFormDto = z.infer<typeof createAdminUserFormSchema>;

/**
 * `GET /admin/users` — серверная пагинация+поиск+фильтры+сортировка (эталон
 * `schemas/payments.ts`). `search` — ilike по tgUsername; `isActive`/`roleId` —
 * точные фильтры; `sortBy` — `createdAt` (дефолт) либо `tgUsername`.
 */
export const adminUserFiltersSchema = paginationSchema.extend({
  search: searchSchema,
  isActive: booleanQuerySchema,
  roleId: z.coerce.number().int().positive().optional(),
  sortBy: z.enum(['createdAt', 'tgUsername']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type AdminUserFiltersDto = z.infer<typeof adminUserFiltersSchema>;

/**
 * POST /admin/users/bulk — массовая активация/деактивация.
 * `ids` — PK AdminUser; action `deactivate` пропускает текущего оператора
 * (self-deactivation forbidden, см. update).
 */
export const adminUserBulkSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(100),
  action: z.enum(['activate', 'deactivate']),
});
export type AdminUserBulkDto = z.infer<typeof adminUserBulkSchema>;
